import { CheckCircle2, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { apiErrorMessage } from '../api/client'
import { invitationApi } from '../api/endpoints'
import { AuthLayout } from '../components/AuthLayout'
import { ErrorMessage } from '../components/ErrorMessage'
import type { ImportInviteView } from '../types/api'

/**
 * Where a bulk-uploaded person's invitation email lands (V51): "Do you want to be a YellowDot?"
 *
 * The email's two buttons both open this page, with ?answer=confirm or ?answer=reject marking the
 * one they pressed - the answer itself is sent only by pressing it here, so a mail scanner that
 * opens every link cannot say yes or no for them. Reject asks once more, because it deletes the
 * account made for them.
 */
export function InvitationPage() {
  const { token = '' } = useParams()
  const [params] = useSearchParams()
  const answer = params.get('answer')
  const [invite, setInvite] = useState<ImportInviteView | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<'confirm' | 'reject' | null>(null)
  const [sureReject, setSureReject] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    invitationApi
      .view(token)
      .then((v) => live && setInvite(v))
      .catch((err) => live && setError(apiErrorMessage(err, 'This invitation link is not valid.')))
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [token])

  async function respond(kind: 'confirm' | 'reject') {
    setError(null)
    setBusy(kind)
    try {
      setInvite(await (kind === 'confirm' ? invitationApi.confirm(token) : invitationApi.reject(token)))
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save your answer. Please try again.'))
    } finally {
      setBusy(null)
      setSureReject(false)
    }
  }

  return (
    <AuthLayout>
      {loading ? (
        <p className="text-ink-500">Loading your invitation...</p>
      ) : !invite ? (
        <>
          <h1 className="text-3xl font-extrabold tracking-tight text-ink-900">Invitation</h1>
          <div className="mt-6">
            <ErrorMessage message={error} />
          </div>
        </>
      ) : invite.status === 'CONFIRMED' ? (
        <>
          <CheckCircle2 size={40} className="text-green-600" aria-hidden="true" />
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink-900">You are a YellowDot</h1>
          <p className="mt-2 text-ink-500">
            Welcome, {invite.fullName}. Your dot is now on the map. Sign in with this email address - we will send
            you a code.
          </p>
          <Link to="/login" className="btn-sun mt-6 w-full">
            Sign in
          </Link>
        </>
      ) : invite.status === 'REJECTED' ? (
        <>
          <XCircle size={40} className="text-ink-400" aria-hidden="true" />
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink-900">No YellowDot made</h1>
          <p className="mt-2 text-ink-500">
            You said no, so the account made for you has been deleted and nothing is shown. If you change your mind,
            you can join yourself at any time.
          </p>
          <Link to="/" className="btn-outline mt-6 w-full">
            Go to YellowDots
          </Link>
        </>
      ) : (
        <>
          <h1 className="text-3xl font-extrabold tracking-tight text-ink-900">Hello {invite.fullName}</h1>
          <p className="mt-2 text-ink-500">
            {invite.invitedBy} would like to add you to YellowDots as a {invite.roleLabel}. Nothing is shown to
            anyone until you say yes.
          </p>
          <p className="mt-5 text-lg font-bold text-ink-900">Do you want to be a YellowDot?</p>

          <div className="mt-4 space-y-3">
            <ErrorMessage message={error} />
            <button
              type="button"
              onClick={() => void respond('confirm')}
              disabled={busy !== null}
              // Focused when they pressed "Yes" in the email, so Enter finishes what they started.
              autoFocus={answer !== 'reject'}
              className={`btn-sun w-full ${answer === 'confirm' ? 'ring-2 ring-dot-600 ring-offset-2' : ''}`}
            >
              {busy === 'confirm' ? 'Confirming...' : 'Yes, confirm'}
            </button>

            {sureReject ? (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                <p>This deletes the account made for you. You can still join yourself later.</p>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => void respond('reject')}
                    disabled={busy !== null}
                    className="rounded-full bg-red-600 px-4 py-2 font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                  >
                    {busy === 'reject' ? 'Deleting...' : 'Yes, reject and delete'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSureReject(false)}
                    className="rounded-full px-4 py-2 font-semibold text-ink-700 hover:text-ink-900"
                  >
                    Go back
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setSureReject(true)}
                disabled={busy !== null}
                autoFocus={answer === 'reject'}
                className={`btn-outline w-full ${answer === 'reject' ? 'ring-2 ring-ink-400 ring-offset-2' : ''}`}
              >
                No, reject
              </button>
            )}
          </div>

          <p className="mt-5 text-sm text-ink-500">
            Confirm, and your dot appears on the map and you can sign in. Reject, and the account made for you is
            deleted.
          </p>
        </>
      )}
    </AuthLayout>
  )
}
